import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpSolnaPage: ServicePageConfig = {
  slug: "flytthjalp-solna",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Solna – Flytt med Skåpbil & Bärare | Ungdomskraft",
    description:
      "Flytthjälp i Solna: Råsunda, Hagalund, Arenastaden, Frösunda, Bergshamra, Huvudsta och Ulriksdal. Skåpbil och bärare, fast pris och RUT-avdrag.",
    h1: "Flytthjälp Solna",
  },

  hero: {
    heading: "Flytthjälp i Solna",
    intro:
      "Solna är tätt, och det märks på flyttdagen. Vi bär ut ur trettiotalshusen i Råsunda, ner från höghusen i Hagalund och in i nybyggena i Arenastaden, Frösunda och Ulriksdal. Ungdomskraft kommer med skåpbil och bärare, kör hela vägen och ställer möblerna på plats. Fast pris i bokningen och RUT-avdrag direkt på fakturan.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt:
      "Ungdomskraft bär flyttkartonger till skåpbil vid flytthjälp i Solna",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Sedan 2014 har Ungdomskraft flyttat hushåll i och runt Stockholm. I Solna jobbar vi ofta i bostadsrättsföreningar med egna regler för hiss och gård, och våra bärare är vana att skydda nymålade trapphus och räkna med att bilen inte alltid kan stå direkt utanför porten.",
    bookYourText: "Boka flytt i Solna",
    bookDescText:
      "Ange båda adresserna, beskriv vad som ska med och välj tid. Priset visas direkt, med skåpbil och bärare.",
    vehicleTitleText: "Bil, bärare och körning",
    vehicleDescText:
      "Vi kommer med skåpbilen, lastar och kör – även när nya adressen ligger på andra sidan E4 eller i en annan kommun. Ingen hyrbil och inga P-avgifter för dig att hålla koll på.",
  },

  itemsSection: {
    heading: "Det här bär vi vid flyttar i Solna",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Soffor som ska runt trånga trapphushörn och in i små hissar",
        image: "/sofa.webp",
        alt: "Soffa som bärs i trapphus vid flytthjälp i Råsunda, Solna",
      },
      {
        name: "Sängar & madrasser",
        description: "Sängar som tas isär och madrasser i skyddsplast",
        image: "/bed.webp",
        alt: "Säng och madrass vid flytt till Arenastaden i Solna",
      },
      {
        name: "Bord & stolar",
        description: "Matbord, hemmakontor och balkongmöbler",
        image: "/table.webp",
        alt: "Skrivbord som flyttas vid flytthjälp i Frösunda, Solna",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskiner, kyl och frys till och från lägenheter",
        image: "/washer.webp",
        alt: "Tvättmaskin som bärs vid flytt i Hagalund, Solna",
      },
      {
        name: "Garderober & byråer",
        description: "Garderober, byråer och bokhyllor som skruvas isär",
        image: "/wardrobe.webp",
        alt: "Garderob som demonteras vid flytt i Bergshamra, Solna",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger från lägenhet, källarförråd och vindsförråd",
        image: "/box.webp",
        alt: "Flyttkartonger lastas i skåpbil vid flytt i Huvudsta, Solna",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Solna – från Råsundas trettiotal till Arenastadens nybyggen",
    mainText:
      "I Solna bor de flesta i lägenhet, och husen är från vitt skilda tider. Råsunda byggdes till stor del under 1920- och 30-talen med stenhus, smala trappor och hissar där en soffa sällan får plats. Hagalunds höga blå punkthus kom på 1960- och 70-talen, liksom mycket av Bergshamra, och där är hissen ofta det som avgör hur snabbt flytten går. Under de senaste åren har helt nya stadsdelar vuxit fram i Arenastaden kring arenan och Mall of Scandinavia, i Frösunda, i Ulriksdal och i den del av Hagastaden som ligger i Solna. Där är hissarna stora men lastgatorna och garagen har egna regler, och det gäller att veta var bilen får stå.\n\nSolna är också en stad där många bor en kortare tid. Karolinska Institutet och Karolinska universitetssjukhuset i Solna drar studenter, forskare och vårdpersonal som flyttar in för en tjänst och vidare när den är slut. Många unga par köper sin första bostadsrätt i Råsunda eller Huvudsta och flyttar vidare till radhus när familjen växer. Vi gör därför allt från små flyttar med två bärare och några möbler till fullstora flyttar där en hel trea ska över till en villa norr om stan.\n\nLäget gör det enkelt att ta sig till och från Solna. E4 och E18 går genom stadens västra delar, Essingeleden börjar här och Norra länken ansluter mot Hagastaden. Tunnelbanans blå linje, tvärbanan till Solna station och pendeltåget i Solna och Ulriksdal gör att många flyttar hit från andra delar av regionen. Vi planerar rutten efter trafiken och lastningen efter gården: gästparkering, lastzon, garageinfart eller kort bärsträcka över innergården. Står det något särskilt i föreningens regler, skriv det i bokningen så följer vi det.",
    preparationHeading: "Förbered flytten i Solna",
    preparationText:
      "Kontakta föreningen eller hyresvärden i god tid och fråga om det finns regler för flyttdagar, hisskydd eller var flyttbilen får stå. I många nya hus i Arenastaden och Frösunda går lastning via garage eller en särskild lastplats, och i Råsundas äldre kvarter kan det vara bättre att boka en tid på förmiddagen när gatan är lugnare. Mät gärna hissen om du är osäker på om soffan får plats.\n\nPacka kartongerna så att de går att stapla och märk dem med rum. Plocka ur hyllor och lådor i byråer innan vi kommer, så blir möblerna lättare att bära i trappan. Töm och frosta av kyl och frys dagen innan. Ha portkod, nycklar och eventuell tagg till garage eller förråd redo för båda adresserna.",
    additionalSections: [
      {
        heading: "Stadsdelar i Solna där vi flyttar",
        content:
          "Vi flyttar i hela Solna stad. I Råsunda, Skytteholm kring Solna centrum och Hagalund är det mest äldre flerbostadshus, där trapphus och hissar kräver planering. I Huvudsta och Bergshamra blandas lägenhetshus med radhus, och i Frösunda, Arenastaden och Ulriksdal är husen nybyggda med garage och lastplatser.\n\nVi flyttar också till och från Hagastaden, Järva och bostäderna nära Hagaparken. Flyttar du från Solna till en annan kommun kör vi hela vägen i samma bokning. Körsträcka inom 20 km ingår, och det räcker för de flesta flyttar inom Stockholms närområde.",
      },
      {
        heading: "Mindre flyttar och studentflyttar i Solna",
        content:
          "Alla flyttar är inte stora. Ska du flytta från ett studentrum eller en etta med några möbler och ett tiotal kartonger räcker ofta paketet Mellan eller Stor med två bärare och skåpbil. Du ser priset direkt, bokar på några minuter och vi är ofta på plats samma vecka.\n\nFlytthjälp ger rätt till RUT-avdrag: 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget dras direkt på fakturan.",
        cta: { text: "Se bortforsling i Solna →", href: "/bortforsling-solna" },
      },
      {
        heading: "Varför boka flytthjälp i Solna med Ungdomskraft?",
        listItems: [
          {
            label: "Vana vid bostadsrättsföreningar –",
            text: "Vi följer föreningens regler för hiss, trapphus och lastning och skyddar gemensamma utrymmen.",
          },
          {
            label: "Nära till allt –",
            text: "E4, E18 och Essingeleden går genom Solna, och vi har bilar i hela regionen. Ofta kan vi komma samma vecka.",
          },
          {
            label: "Fast pris –",
            text: "Priset står i bokningen innan du bekräftar. Inga tillägg för våningsplan eller trånga trappor.",
          },
          {
            label: "Försäkrad flytt –",
            text: "Bohag, trapphus och fastighet är försäkrade genom Trygg Hansa under hela uppdraget.",
          },
          {
            label: "Allt ingår –",
            text: "Skåpbil, bärare, filtar, remmar och spännband. Du behöver inte hyra något själv.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Solna?",
      answer:
        "En mindre flytt med några möbler ryms ofta i Liten (995 kr) eller Mellan (1 495 kr). En fullt möblerad etta eller tvåa hamnar oftast på 2 495–4 995 kr, och en hel lägenhet med mycket bohag bokas som Mega Stor (8 995 kr). Körning inom 20 km ingår och priset syns innan du bokar.",
    },
    {
      question: "Får jag RUT-avdrag på flytthjälp i Solna?",
      answer:
        "Ja, flytthjälp ger 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det direkt på fakturan.",
    },
    {
      question: "Hur snabbt kan ni hjälpa mig att flytta i Solna?",
      answer:
        "Vi har ofta lediga tider samma vecka och ibland samma dag. Välj datum i bokningen så ser du tiderna direkt.",
    },
    {
      question: "Hissen i vårt hus i Råsunda är liten – går det ändå?",
      answer:
        "Ja. Det som inte får plats i hissen bär vi i trappan, och möbler som behöver tas isär demonterar vi på plats. Trappor kostar inget extra.",
    },
    {
      question: "Kan ni flytta in i en nyproduktion i Arenastaden eller Frösunda?",
      answer:
        "Ja. Berätta i bokningen om lastning sker via garage eller lastplats och om det finns en inflyttningstid att passa, så planerar vi efter det.",
    },
    {
      question: "Följer ni föreningens regler för flytt?",
      answer:
        "Ja. Skriv i bokningen om det finns regler för hisskydd, tider eller var bilen får stå. Vi har med skyddsmattor och filtar för trapphus och hiss.",
    },
    {
      question: "Kan jag få hjälp med en studentflytt i Solna?",
      answer:
        "Ja, en liten flytt från ett studentrum eller en etta bokas som ett mindre paket med två bärare och skåpbil. Du ser priset direkt.",
    },
    {
      question: "Demonterar och monterar ni möbler?",
      answer:
        "Ja, sängar, garderober och bord tas isär när det behövs och sätts ihop igen i nya hemmet.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, tvättmaskin, torktumlare, kyl, frys och diskmaskin. Frosta av kyl och frys dygnet innan.",
    },
    {
      question: "Kan ni flytta på helger?",
      answer: "Ja, vi flyttar alla dagar, även lördagar och söndagar.",
    },
    {
      question: "Hjälper ni med kontorsflytt i Solna?",
      answer:
        "Ja, vi gör mindre kontorsflyttar i Solna, till exempel i Frösunda och Arenastaden: skrivbord, stolar, arkivskåp och kartonger.",
    },
    {
      question: "Vilka stadsdelar i Solna täcker ni?",
      answer:
        "Hela Solna: Råsunda, Skytteholm, Hagalund, Huvudsta, Bergshamra, Frösunda, Arenastaden, Ulriksdal, Hagastaden, Haga och Järva.",
    },
    {
      question: "Vilka andra kommuner hjälper ni till i?",
      answer: `Vi utför flytthjälp i hela Stockholmsregionen: ${stockholmKommunerText}. Körsträcka inom 20 km ingår alltid.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Solna – så enkelt är det",
    step2Text:
      "Vi skyddar hiss och trapphus, bär ut allt och lastar skåpbilen. Sedan kör vi direkt till nya adressen, oavsett om den ligger i Solna eller längre bort.",
    step3Title: "På plats",
    step3Desc:
      "Vi bär in möblerna och kartongerna, ställer dem där du vill och monterar ihop det vi tog isär.",
    ctaHeading: "Boka flytthjälp i Solna",
    ctaText:
      "Skriv in adresserna, välj paket och tid. Priset syns direkt och RUT-avdraget dras på fakturan.",
  },

  en: {
    slug: "moving-help-solna",
    meta: {
      title: "Moving Help Solna – Van & Movers | Ungdomskraft",
      description:
        "Moving help in Solna: Råsunda, Hagalund, Arenastaden, Frösunda, Bergshamra, Huvudsta and Ulriksdal. Van and movers, fixed price and RUT deduction.",
      h1: "Moving Help Solna",
    },
    hero: {
      heading: "Moving Help in Solna",
      intro:
        "Solna is dense, and you feel it on moving day. We carry out of the 1930s buildings in Råsunda, down from the high-rises in Hagalund and into the new builds in Arenastaden, Frösunda and Ulriksdal. Ungdomskraft arrives with a van and movers, drives the whole way and puts the furniture in place. Fixed price in the booking and the RUT deduction straight on the invoice.",
      heroImageAlt: "Ungdomskraft carrying moving boxes to a van during a move in Solna",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Since 2014 Ungdomskraft has moved households in and around Stockholm. In Solna we often work in housing cooperatives with their own rules for lifts and courtyards, and our movers are used to protecting freshly painted stairwells and knowing the van can't always stand right outside the door.",
      bookYourText: "Book a move in Solna",
      bookDescText:
        "Enter both addresses, describe what's coming and pick a time. The price shows instantly, with van and movers.",
      vehicleTitleText: "Van, movers and driving",
      vehicleDescText:
        "We arrive with the van, load and drive — even when the new address is on the other side of the E4 or in another municipality. No rental van and no parking fees for you to track.",
    },
    itemsSection: {
      heading: "What we carry on moves in Solna",
      items: [
        {
          name: "Sofas & armchairs",
          description: "Sofas that need to get round tight stairwell corners and into small lifts",
          image: "/sofa.webp",
          alt: "Sofa carried in a stairwell during moving help in Råsunda, Solna",
        },
        {
          name: "Beds & mattresses",
          description: "Beds taken apart and mattresses in protective plastic",
          image: "/bed.webp",
          alt: "Bed and mattress during a move to Arenastaden in Solna",
        },
        {
          name: "Tables & chairs",
          description: "Dining tables, home offices and balcony furniture",
          image: "/table.webp",
          alt: "Desk moved during moving help in Frösunda, Solna",
        },
        {
          name: "Appliances",
          description: "Washers, fridges and freezers to and from flats",
          image: "/washer.webp",
          alt: "Washing machine carried during a move in Hagalund, Solna",
        },
        {
          name: "Wardrobes & dressers",
          description: "Wardrobes, dressers and bookcases unscrewed for transport",
          image: "/wardrobe.webp",
          alt: "Wardrobe being dismantled during a move in Bergshamra, Solna",
        },
        {
          name: "Moving boxes",
          description: "Boxes from the flat, basement storage and loft storage",
          image: "/box.webp",
          alt: "Moving boxes loaded into a van during a move in Huvudsta, Solna",
        },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Solna — from 1930s Råsunda to new-build Arenastaden",
      mainText:
        "Most people in Solna live in flats, and the buildings come from very different eras. Råsunda was largely built in the 1920s and 30s with stone buildings, narrow stairs and lifts where a sofa rarely fits. Hagalund's tall blue tower blocks arrived in the 1960s and 70s, as did much of Bergshamra, and there the lift is often what decides how fast the move goes. In recent years whole new districts have grown up in Arenastaden around the arena and Mall of Scandinavia, in Frösunda, in Ulriksdal and in the part of Hagastaden that lies in Solna. There the lifts are big, but the loading streets and garages have their own rules, and you need to know where the van may stand.\n\nSolna is also a city where many people stay for a shorter time. Karolinska Institutet and Karolinska University Hospital in Solna attract students, researchers and healthcare staff who move in for a post and move on when it ends. Many young couples buy their first flat in Råsunda or Huvudsta and move on to a terraced house when the family grows. So we do everything from small moves with two movers and a few pieces to full moves where a whole three-room flat goes to a house north of the city.\n\nThe location makes it easy to get in and out of Solna. The E4 and E18 run through the western parts of the city, Essingeleden starts here and Norra länken connects towards Hagastaden. The blue metro line, the cross-city tram (tvärbanan) to Solna station and the commuter trains at Solna and Ulriksdal mean many people move here from other parts of the region. We plan the route around the traffic and the loading around the courtyard: guest parking, a loading zone, a garage entrance or a short carry across the yard. If your cooperative has special rules, add them to the booking and we'll follow them.",
      preparationHeading: "Prepare your move in Solna",
      preparationText:
        "Contact your housing cooperative or landlord in good time and ask whether there are rules for moving days, lift protection or where the moving van may park. In many new buildings in Arenastaden and Frösunda loading goes via the garage or a dedicated loading spot, and in Råsunda's older blocks a morning slot can be better when the street is quieter. Measure the lift if you're unsure whether the sofa will fit.\n\nPack boxes so they stack and label them by room. Empty shelves and dresser drawers before we arrive so the furniture is lighter on the stairs. Empty and defrost the fridge and freezer the day before. Have door codes, keys and any fob for the garage or storage ready for both addresses.",
      additionalSections: [
        {
          heading: "Districts in Solna where we move",
          content:
            "We move across the whole of Solna. In Råsunda, Skytteholm around Solna centrum and Hagalund it's mostly older apartment blocks, where stairwells and lifts need planning. Huvudsta and Bergshamra mix apartment blocks with terraced houses, and in Frösunda, Arenastaden and Ulriksdal the buildings are new with garages and loading spots.\n\nWe also move to and from Hagastaden, Järva and the homes near Hagaparken. Moving from Solna to another municipality? We drive the whole way in the same booking. Driving within 20 km is included, which covers most moves within the Stockholm area.",
        },
        {
          heading: "Small moves and student moves in Solna",
          content:
            "Not every move is big. Moving out of a student room or a studio with a few pieces of furniture and a dozen boxes usually fits the Medium or Large package with two movers and a van. You see the price instantly, book in a few minutes and we're often there the same week.\n\nMoving help qualifies for the RUT tax deduction: 50% of the labour cost, up to SEK 75,000 per person per year. The deduction comes straight off the invoice.",
          cta: { text: "See junk removal in Solna →", href: "/en/junk-removal-solna" },
        },
        {
          heading: "Why book moving help in Solna with Ungdomskraft?",
          listItems: [
            {
              label: "Used to housing cooperatives –",
              text: "We follow the cooperative's rules for lifts, stairwells and loading and protect shared spaces.",
            },
            {
              label: "Close to everything –",
              text: "The E4, E18 and Essingeleden run through Solna, and we have vans across the region. We can often come the same week.",
            },
            {
              label: "Fixed price –",
              text: "The price is in the booking before you confirm. No surcharge for floors or tight stairs.",
            },
            {
              label: "Insured move –",
              text: "Belongings, stairwell and property are insured through Trygg Hansa for the whole job.",
            },
            {
              label: "Everything included –",
              text: "Van, movers, blankets, straps and tie-downs. You don't need to rent anything yourself.",
            },
          ],
        },
      ],
    },
    faq: [
      {
        question: "How much does moving help in Solna cost?",
        answer:
          "A small move with a few pieces usually fits Small (SEK 995) or Medium (SEK 1,495). A fully furnished studio or one-bed usually lands at SEK 2,495–4,995, and a whole flat with lots of belongings is booked as Mega Large (SEK 8,995). Driving within 20 km is included and the price shows before you book.",
      },
      {
        question: "Do I get the RUT deduction on moving help in Solna?",
        answer:
          "Yes, moving help gives a 50% deduction on the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice.",
      },
      {
        question: "How quickly can you help me move in Solna?",
        answer:
          "We often have slots the same week and sometimes the same day. Pick a date in the booking to see times straight away.",
      },
      {
        question: "The lift in our Råsunda building is tiny — will it work?",
        answer:
          "Yes. Whatever doesn't fit in the lift we carry on the stairs, and furniture that needs taking apart we dismantle on site. Stairs cost nothing extra.",
      },
      {
        question: "Can you move me into a new build in Arenastaden or Frösunda?",
        answer:
          "Yes. Tell us in the booking whether loading is via the garage or a loading spot and whether there's a move-in time to meet, and we'll plan around it.",
      },
      {
        question: "Do you follow the cooperative's moving rules?",
        answer:
          "Yes. Note any rules on lift protection, times or where the van may stand in the booking. We bring floor protection and blankets for the stairwell and lift.",
      },
      {
        question: "Can I get help with a student move in Solna?",
        answer:
          "Yes, a small move from a student room or studio is booked as a smaller package with two movers and a van. You see the price instantly.",
      },
      {
        question: "Do you dismantle and reassemble furniture?",
        answer:
          "Yes, beds, wardrobes and tables are taken apart when needed and put back together in your new home.",
      },
      {
        question: "Do you move appliances?",
        answer:
          "Yes — washing machines, dryers, fridges, freezers and dishwashers. Defrost the fridge and freezer the day before.",
      },
      {
        question: "Can you move at weekends?",
        answer: "Yes, we move every day, including Saturdays and Sundays.",
      },
      {
        question: "Do you help with office moves in Solna?",
        answer:
          "Yes, we do smaller office moves in Solna, for example in Frösunda and Arenastaden: desks, chairs, filing cabinets and boxes.",
      },
      {
        question: "Which districts in Solna do you cover?",
        answer:
          "All of Solna: Råsunda, Skytteholm, Hagalund, Huvudsta, Bergshamra, Frösunda, Arenastaden, Ulriksdal, Hagastaden, Haga and Järva.",
      },
      {
        question: "Which other municipalities do you help in?",
        answer: `We provide moving help across the Stockholm region: ${stockholmKommunerText}. Driving within 20 km is always included.`,
      },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Solna — this simple",
      step2Text:
        "We protect the lift and stairwell, carry everything out and load the van. Then we drive straight to the new address, whether it's in Solna or further away.",
      step3Title: "In place",
      step3Desc:
        "We carry in the furniture and boxes, put them where you want and reassemble anything we took apart.",
      ctaHeading: "Book moving help in Solna",
      ctaText:
        "Enter the addresses, choose a package and a time. The price shows instantly and the RUT deduction comes off the invoice.",
    },
  },
};
